import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import QRCode from "qrcode";
import { API } from "../context/AuthContext";
import { Loader2, Printer, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";

const TEMPLATE_BG = "/permit_template_no_banners.png?v=24";

const MESES_ES = [
  "ENERO", "FEBRERO", "MARZO", "ABRIL", "MAYO", "JUNIO",
  "JULIO", "AGOSTO", "SEPTIEMBRE", "OCTUBRE", "NOVIEMBRE", "DICIEMBRE",
];

function formatDateEs(dateStr) {
  const d = new Date(dateStr);
  return `${String(d.getDate()).padStart(2, "0")} DE ${MESES_ES[d.getMonth()]} DE ${d.getFullYear()}`;
}

export default function PrintPermit() {
  const { folio } = useParams();
  const [search] = useSearchParams();
  const queryToken = search.get("token");
  const localToken = localStorage.getItem("transito_token");
  const token = queryToken || localToken;

  const [permit, setPermit] = useState(null);
  const [error, setError] = useState("");
  const [qrDataUrl, setQrDataUrl] = useState("");

  useEffect(() => {
    if (!token) {
      setError("No autorizado. Inicia sesión.");
      return;
    }
    axios
      .get(`${API}/permits`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        const found = r.data.find((p) => p.folio_short === folio);
        if (!found) setError("Permiso no encontrado");
        else setPermit(found);
      })
      .catch(() => setError("Error al cargar permiso"));
  }, [folio, token]);

  useEffect(() => {
    if (!permit) return;
    const validationUrl = `${window.location.origin}/vd-qr/${permit.folio_short}/${permit.qr_token}`;
    QRCode.toDataURL(validationUrl, { width: 800, margin: 1 }).then(setQrDataUrl).catch(() => {});
  }, [permit]);

  const handlePrint = () => {
    // Open backend-generated vector PDF (sharper than HTML print)
    const url = `${API}/permits/${permit.folio_short}/pdf?token=${encodeURIComponent(token)}&inline=1`;
    window.open(url, "_blank");
  };

  const handlePrintHtml = () => window.print();

  if (error) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <p style={{ color: "#b91c1c" }}>{error}</p>
        <Button onClick={() => window.history.back()}>
          <ArrowLeft size={14} style={{ marginRight: 6 }} /> Volver
        </Button>
      </div>
    );
  }
  if (!permit) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: "#6b7280" }}>
        <Loader2 size={28} className="spin" /> Cargando permiso...
      </div>
    );
  }

  const year = new Date(permit.created_at).getFullYear();

  return (
    <div className="print-wrapper">
      {/* Action bar - hidden on print */}
      <div
        className="no-print"
        style={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          backgroundColor: "white",
          borderBottom: "1px solid #e5e7eb",
          padding: "0.75rem 1rem",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
        }}
      >
        <Button variant="outline" onClick={() => window.history.back()}>
          <ArrowLeft size={14} style={{ marginRight: 6 }} /> Volver
        </Button>
        <div style={{ display: "flex", gap: 8 }}>
          <Button variant="outline" onClick={handlePrintHtml} title="Imprimir esta vista HTML">
            <Printer size={14} style={{ marginRight: 6 }} /> Impresión rápida
          </Button>
          <Button onClick={handlePrint} style={{ backgroundColor: "#f58238", color: "white" }} title="Abre el PDF profesional vectorial — máxima calidad">
            <Printer size={14} style={{ marginRight: 6 }} /> Imprimir / Guardar PDF
          </Button>
        </div>
      </div>

      {/* Permit page (letter-size with template as background) */}
      <div className="permit-page">
        {/* Template image as <img> so it always prints */}
        <img className="template-bg" src={TEMPLATE_BG} alt="" aria-hidden="true" />

        {/* Top orange ribbon */}
        <div className="ribbon-top">VÁLIDO POR 30 DÍAS</div>

        {/* IDC badge top-right */}
        <div className="idc-badge">IDC/{permit.folio_short}/{year}</div>

        {/* Folio único value (the label "Folio único:" comes from template image) */}
        <div className="folio-value">{permit.folio_short}</div>

        {/* Date range - large red, centered */}
        <div className="date-range">
          {formatDateEs(permit.created_at)} AL {formatDateEs(permit.expires_at)}
        </div>

        {/* Vehicle data (labels are in template image, only values overlaid) */}
        <div className="field field-serie">{(permit.serie || "").toUpperCase()}</div>
        <div className="field field-motor">{(permit.motor_number || "").toUpperCase()}</div>
        <div className="field field-marca">{(permit.marca || "").toUpperCase()}</div>
        <div className="field field-modelo">{String(permit.modelo || "").toUpperCase()}</div>
        <div className="field field-linea">{(permit.linea || "").toUpperCase()}</div>
        <div className="field field-contrib">{(permit.owner_name || "").toUpperCase()}</div>

        {/* QR code */}
        <div className="qr-overlay">
          {qrDataUrl ? <img src={qrDataUrl} alt="QR validación" /> : null}
        </div>

        {/* Tránsito logo below QR */}
        <div className="transito-logo">
          <img src="/transito_logo.png" alt="Tránsito Ixcateopan" />
        </div>

        {/* Bottom orange ribbon */}
        <div className="ribbon-bottom">2026 — 2030</div>
      </div>

      <style>{`
        .print-wrapper {
          background-color: #e9ecef;
          min-height: 100vh;
          font-family: 'Poppins', sans-serif;
          padding: 16px 0;
        }
        .permit-page {
          position: relative;
          width: 8.5in;
          height: 11in;
          margin: 0 auto;
          background-color: white;
          background-image:
            url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='180' height='180' viewBox='0 0 180 180'><defs><pattern id='p' x='0' y='0' width='45' height='45' patternUnits='userSpaceOnUse'><circle cx='22' cy='22' r='2.0' fill='%23a87f4f' opacity='0.65'/><circle cx='5' cy='5' r='1.4' fill='%23a87f4f' opacity='0.55'/><circle cx='38' cy='8' r='1.3' fill='%23a87f4f' opacity='0.55'/><circle cx='10' cy='38' r='1.2' fill='%23a87f4f' opacity='0.50'/></pattern></defs><rect width='180' height='180' fill='url(%23p)'/></svg>");
          background-size: 180px 180px;
          background-repeat: repeat;
          box-shadow: 0 4px 20px rgba(0,0,0,0.12);
          color: #1a1a1a;
          overflow: hidden;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        .template-bg {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: fill;
          z-index: 0;
          pointer-events: none;
          image-rendering: -webkit-optimize-contrast;
          image-rendering: crisp-edges;
        }
        .qr-overlay img,
        .transito-logo img {
          image-rendering: -webkit-optimize-contrast;
          image-rendering: crisp-edges;
        }
        .permit-page,
        .permit-page * {
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          text-rendering: geometricPrecision;
        }
        .permit-page > *:not(.template-bg) {
          position: absolute;
          z-index: 1;
        }
        /* Use pt units so they match PDF coordinates exactly (612x791 pt page) */
        .ribbon-top, .ribbon-bottom {
          position: absolute;
          left: 0;
          right: 0;
          background-color: #f58238;
          color: white;
          font-weight: 900;
          text-align: center;
          letter-spacing: 4px;
          font-size: 24pt;
          padding: 10pt 0;
          font-family: 'Poppins', sans-serif;
          text-transform: uppercase;
        }
        .ribbon-top { top: 0; }
        .ribbon-bottom { bottom: 0; font-size: 18pt; letter-spacing: 6px; padding: 7pt 0; }

        .idc-badge {
          position: absolute;
          top: 85pt;
          left: 408pt;
          font-weight: 800;
          font-size: 12pt;
          color: #1a1a1a;
        }

        .folio-value {
          position: absolute;
          top: 128pt;
          left: 408pt;
          font-weight: 800;
          font-size: 12pt;
          color: #a01818;
        }

        .date-range {
          position: absolute;
          top: 348pt;
          left: 0;
          right: 0;
          text-align: center;
          font-weight: 900;
          font-size: 22pt;
          color: #a01818;
          letter-spacing: 0.5px;
        }

        .field {
          position: absolute;
          left: 170pt;
          font-weight: 900;
          font-size: 12pt;
          color: #1a1a1a;
          line-height: 1;
          white-space: nowrap;
        }
        .field-serie    { top: 458pt; }
        .field-motor    { top: 478pt; }
        .field-marca    { top: 498pt; }
        .field-modelo   { top: 518pt; }
        .field-linea    { top: 538pt; }
        .field-contrib  { top: 558pt; }

        .qr-overlay {
          position: absolute;
          top: 425pt;
          left: 410pt;
          width: 150pt;
          height: 150pt;
        }
        .qr-overlay img {
          width: 100%;
          height: 100%;
          display: block;
        }

        .transito-logo {
          position: absolute;
          top: 605pt;
          left: 70pt;
          width: 70pt;
          height: 70pt;
          opacity: 1;
        }
        .transito-logo img {
          width: 100%;
          height: 100%;
          display: block;
          object-fit: contain;
        }

        /* Print rules */
        @media print {
          .no-print { display: none !important; }
          .print-wrapper { background: white !important; padding: 0 !important; }
          .permit-page {
            margin: 0 !important;
            box-shadow: none !important;
            width: 100% !important;
            height: 100vh !important;
          }
          @page {
            size: letter;
            margin: 0;
          }
          body { margin: 0 !important; }
          .permit-page,
          .ribbon-top,
          .ribbon-bottom,
          .template-bg {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
        }
      `}</style>
    </div>
  );
}
